import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["password", "confirmation", "submit", "passwordFeedback", "confirmationFeedback", "strengthBar", "strengthText", "strengthContainer"]

  connect() {
    this.validate()
  }

  validate() {
    const password = this.passwordTarget.value
    const confirmation = this.confirmationTarget.value
    const minLength = 6

    // Check password strength
    this.updatePasswordStrength(password, minLength)

    // Check password confirmation match
    this.updateConfirmationMatch(password, confirmation, minLength)

    // Enable/disable submit button
    this.updateSubmitButton(password, confirmation, minLength)
  }

  updatePasswordStrength(password, minLength) {
    if (!this.hasPasswordFeedbackTarget) return

    const length = password.length
    
    if (length === 0) {
      this.hidePasswordFeedback()
      this.hideStrengthIndicator()
      return
    }

    // Show strength indicator
    if (this.hasStrengthBarTarget && this.hasStrengthTextTarget) {
      this.showStrengthIndicator()
      const strength = this.calculatePasswordStrength(password)
      this.updateStrengthBar(strength)
    }

    // Show length validation
    if (length < minLength) {
      this.showPasswordFeedback('warning', `At least ${minLength} characters required`)
    } else {
      this.showPasswordFeedback('success', 'Password meets minimum requirements')
    }
  }

  updateConfirmationMatch(password, confirmation, minLength) {
    if (!this.hasConfirmationFeedbackTarget) return

    const confirmationLength = confirmation.length

    if (confirmationLength === 0) {
      this.hideConfirmationFeedback()
      return
    }

    if (password.length < minLength) {
      this.showConfirmationFeedback('warning', 'Complete password first')
    } else if (password === confirmation) {
      this.showConfirmationFeedback('success', 'Passwords match')
    } else {
      this.showConfirmationFeedback('error', 'Passwords do not match')
    }
  }

  updateSubmitButton(password, confirmation, minLength) {
    const isValid = password.length >= minLength && 
                    confirmation.length >= minLength && 
                    password === confirmation

    if (isValid) {
      this.submitTarget.disabled = false
      this.submitTarget.classList.remove('opacity-50', 'cursor-not-allowed')
    } else {
      this.submitTarget.disabled = true
      this.submitTarget.classList.add('opacity-50', 'cursor-not-allowed')
    }
  }

  calculatePasswordStrength(password) {
    let strength = 0
    
    // Length
    if (password.length >= 6) strength += 25
    if (password.length >= 10) strength += 25
    
    // Contains lowercase
    if (/[a-z]/.test(password)) strength += 15
    
    // Contains uppercase
    if (/[A-Z]/.test(password)) strength += 15
    
    // Contains numbers
    if (/\d/.test(password)) strength += 10
    
    // Contains special characters
    if (/[^A-Za-z0-9]/.test(password)) strength += 10

    return Math.min(strength, 100)
  }

  updateStrengthBar(strength) {
    const bar = this.strengthBarTarget
    const text = this.strengthTextTarget
    
    bar.style.width = `${strength}%`
    
    // Remove all strength classes
    bar.classList.remove('bg-red-500', 'bg-yellow-500', 'bg-green-500')
    
    // Add appropriate class based on strength
    if (strength < 40) {
      bar.classList.add('bg-red-500')
      text.textContent = 'Weak'
      text.classList.remove('text-yellow-400', 'text-green-400')
      text.classList.add('text-red-400')
    } else if (strength < 70) {
      bar.classList.add('bg-yellow-500')
      text.textContent = 'Medium'
      text.classList.remove('text-red-400', 'text-green-400')
      text.classList.add('text-yellow-400')
    } else {
      bar.classList.add('bg-green-500')
      text.textContent = 'Strong'
      text.classList.remove('text-red-400', 'text-yellow-400')
      text.classList.add('text-green-400')
    }
  }

  showPasswordFeedback(type, message) {
    const feedback = this.passwordFeedbackTarget
    const input = this.passwordTarget
    
    feedback.textContent = message
    feedback.classList.remove('hidden')
    
    // Update input border
    input.classList.remove('border-zinc-700', 'border-red-500', 'border-green-500', 'border-yellow-500')
    
    if (type === 'success') {
      feedback.classList.remove('text-red-400', 'text-yellow-400')
      feedback.classList.add('text-green-400')
      input.classList.add('border-green-500')
    } else if (type === 'error') {
      feedback.classList.remove('text-green-400', 'text-yellow-400')
      feedback.classList.add('text-red-400')
      input.classList.add('border-red-500')
    } else if (type === 'warning') {
      feedback.classList.remove('text-green-400', 'text-red-400')
      feedback.classList.add('text-yellow-400')
      input.classList.add('border-yellow-500')
    }
  }

  showConfirmationFeedback(type, message) {
    const feedback = this.confirmationFeedbackTarget
    const input = this.confirmationTarget
    
    feedback.textContent = message
    feedback.classList.remove('hidden')
    
    // Update input border
    input.classList.remove('border-zinc-700', 'border-red-500', 'border-green-500', 'border-yellow-500')
    
    if (type === 'success') {
      feedback.classList.remove('text-red-400', 'text-yellow-400')
      feedback.classList.add('text-green-400')
      input.classList.add('border-green-500')
    } else if (type === 'error') {
      feedback.classList.remove('text-green-400', 'text-yellow-400')
      feedback.classList.add('text-red-400')
      input.classList.add('border-red-500')
    } else if (type === 'warning') {
      feedback.classList.remove('text-green-400', 'text-red-400')
      feedback.classList.add('text-yellow-400')
      input.classList.add('border-yellow-500')
    }
  }

  hidePasswordFeedback() {
    if (this.hasPasswordFeedbackTarget) {
      this.passwordFeedbackTarget.classList.add('hidden')
      this.passwordTarget.classList.remove('border-red-500', 'border-green-500', 'border-yellow-500')
      this.passwordTarget.classList.add('border-zinc-700')
    }
  }

  hideConfirmationFeedback() {
    if (this.hasConfirmationFeedbackTarget) {
      this.confirmationFeedbackTarget.classList.add('hidden')
      this.confirmationTarget.classList.remove('border-red-500', 'border-green-500', 'border-yellow-500')
      this.confirmationTarget.classList.add('border-zinc-700')
    }
  }

  showStrengthIndicator() {
    if (this.hasStrengthContainerTarget) {
      this.strengthContainerTarget.classList.remove('hidden')
    }
  }

  hideStrengthIndicator() {
    if (this.hasStrengthContainerTarget) {
      this.strengthContainerTarget.classList.add('hidden')
    }
    if (this.hasStrengthBarTarget) {
      this.strengthBarTarget.style.width = '0%'
    }
  }
}
